import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HeartPulse, ArrowLeft, Eye, EyeOff, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const navigate = useNavigate();
  const { setSession } = useApp();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError("Lien d'invitation manquant ou invalide."); setLoading(false); return; }
    axios.get(`${API}/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Invitation introuvable"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Mot de passe minimum 6 caractères"); return; }
    if (pw !== confirmPw) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/accept-invite`, { token, password: pw });
      if (setSession) setSession(r.data.token, r.data.user);
      setDone(true);
      toast.success("Compte activé. Bienvenue !");
      setTimeout(() => {
        if (r.data.user.role === "super_admin") navigate("/super-admin");
        else navigate("/dashboard");
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'activation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1665231795856-769fb08a90bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvciUyMHdhcm18ZW58MHx8fHwxNzc4MzIyMzAyfDA&ixlib=rb-4.1.0&q=85"
          alt="Clinic" className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1E4631]/85 via-[#1E4631]/40 to-transparent"/>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white grid place-items-center">
              <HeartPulse className="w-5 h-5 text-[#1E4631]"/>
            </div>
            <span className="font-[Outfit] font-bold text-2xl">KWIKILA Care</span>
          </div>
          <h2 className="font-[Outfit] text-3xl font-bold leading-tight max-w-md">
            Rejoignez l'équipe qui transforme l'accueil hospitalier en RDC.
          </h2>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="p-6">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#5C6B61] hover:text-[#1E4631]" data-testid="back-login">
            <ArrowLeft className="w-4 h-4"/> Connexion
          </Link>
        </div>

        <div className="flex-1 grid place-items-center px-6 pb-12">
          <div className="w-full max-w-md space-y-6">
            {loading ? (
              <div className="text-center py-12" data-testid="invite-loading">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1E4631]"/>
                <p className="mt-4 text-[#5C6B61] text-sm">Chargement de l'invitation…</p>
              </div>
            ) : error ? (
              <div className="bg-white rounded-2xl border border-[#E5EBE7] p-8 text-center space-y-4" data-testid="invite-error">
                <div className="w-14 h-14 rounded-full bg-[#C62828]/10 grid place-items-center mx-auto">
                  <ShieldCheck className="w-7 h-7 text-[#C62828]"/>
                </div>
                <h2 className="text-2xl font-[Outfit] font-bold">Lien invalide</h2>
                <p className="text-sm text-[#5C6B61]">{error}</p>
                <Link to="/login"><Button variant="outline" className="mt-2">Aller à la connexion</Button></Link>
              </div>
            ) : done ? (
              <div className="bg-white rounded-2xl border border-[#E5EBE7] p-8 text-center space-y-4" data-testid="invite-done">
                <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 grid place-items-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-[#2E7D32]"/>
                </div>
                <h2 className="text-2xl font-[Outfit] font-bold">Compte activé !</h2>
                <p className="text-sm text-[#5C6B61]">Redirection en cours…</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="invite-form">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D96C4A]/10 text-[#D96C4A] text-xs font-semibold mb-3">
                    <ShieldCheck className="w-3.5 h-3.5"/> Invitation
                  </div>
                  <h1 className="text-3xl font-[Outfit] font-bold">Activer mon compte</h1>
                  <div className="mt-4 p-4 rounded-xl bg-[#1E4631]/5 border border-[#1E4631]/10 space-y-1 text-sm" data-testid="invite-info">
                    <div><span className="text-[#5C6B61]">Nom :</span> <strong>{invite.name}</strong></div>
                    <div><span className="text-[#5C6B61]">Email :</span> <strong>{invite.email}</strong></div>
                    <div><span className="text-[#5C6B61]">Rôle :</span> <strong>{invite.role_label}</strong></div>
                    {invite.hospital_name && <div><span className="text-[#5C6B61]">Hôpital :</span> <strong>{invite.hospital_name}</strong></div>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inv-pw">Choisissez votre mot de passe</Label>
                  <div className="relative">
                    <Input id="inv-pw" type={showPw ? "text" : "password"} required minLength={6}
                           value={pw} onChange={(e)=>setPw(e.target.value)}
                           className="pr-10" data-testid="invite-pw"/>
                    <button type="button" onClick={()=>setShowPw(v=>!v)}
                            aria-label={showPw ? "Masquer" : "Afficher"}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-[#5C6B61] hover:text-[#1E4631] hover:bg-[#1E4631]/5 transition"
                            data-testid="invite-toggle-pw">
                      {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inv-confirm">Confirmer le mot de passe</Label>
                  <Input id="inv-confirm" type={showPw ? "text" : "password"} required minLength={6}
                         value={confirmPw} onChange={(e)=>setConfirmPw(e.target.value)}
                         data-testid="invite-confirm"/>
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-[#1E4631] hover:bg-[#163424] text-white py-6 rounded-lg font-semibold" data-testid="invite-submit">
                  {submitting ? "..." : "Activer mon compte"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
